<device-status-control>
  <!-- Card 2: Estado Actual y Control -->
  <div class="card h-100">
    <div class="card-header bg-white d-flex justify-content-between align-items-center gap-3">
      <span class="fw-semibold">Control del dispositivo</span>
      <button
        type="button"
        class="btn btn-outline-secondary btn-sm"
        onclick={ () => props.onrefreshstate() }
        disabled={ !props.isonline || props.commandstatusloading }
        title="Solicitar estado actual al dispositivo"
      >
        <i class="bi bi-arrow-clockwise me-1"></i> Actualizar estado
      </button>
    </div>
    <div class="card-body device-control-body">
      <div if={ hasSignals() }>
        <section if={ getOutputChannels().length > 0 } class="device-control-signal-section mb-4">
          <div class="row g-3">
            <channel-control
              each={ channel in getOutputChannels() }
              channel={ channel }
              isOnline={ props.isonline }
              onOpenDoor={ props.onopendoor }
              onEditSchedule={ props.oneditschedule }
              onShowScheduleHelp={ props.onshowschedulehelp }
            />
          </div>

          <div if={ getOutputChannels().length > 1 } class="d-flex justify-content-between align-items-center gap-3 flex-wrap mt-3 pt-3 border-top">
            <div>
              <strong>Apertura secuencial</strong>
              <small class="d-block text-muted">Pulso en ambas salidas segun la configuracion del dispositivo.</small>
            </div>
            <button
              class="btn btn-outline-primary btn-sm"
              onclick={ handlePulseSeq }
              disabled={ !props.isonline || props.commandstatusloading }
              title="Ejecutar pulso secuencial en out0 y out1"
            >
              <i class="bi bi-collection-play me-1"></i> Apertura secuencial
            </button>
          </div>
        </section>

        <section if={ getInputSignals().length > 0 } class="device-control-signal-section">
          <div if={ hasInputReadings() } class="row g-3">
            <div each={ signal in getInputSignals() } class="col-12 col-md-6">
              <div class="device-input-card { getInputSignalVariant(signal.channel) } h-100">
                <div class="d-flex justify-content-between align-items-start gap-3">
                  <div>
                    <div class="d-flex align-items-center gap-2 mb-2">
                      <span class="device-input-pill { getInputSignalVariant(signal.channel) }"></span>
                      <strong>{ signal.name }</strong>
                      <span class="badge bg-secondary rounded-pill fw-normal">{ signal.channel }</span>
                    </div>
                    <div class="device-input-state-row">
                      <span class="device-input-state-label">Estado actual</span>
                      <span class="device-input-state-badge { getInputSignalVariant(signal.channel) }">{ getInputSignalText(signal.channel) }</span>
                    </div>
                  </div>
                  <i class="bi { getInputSignalIcon(signal.channel) } device-input-icon"></i>
                </div>
                <p class="device-input-copy mb-0 mt-3">{ getInputSignalHelp(signal.channel) }</p>
              </div>
            </div>
          </div>

          <div if={ !hasInputReadings() } class="device-control-empty-state">
            <i class="bi bi-diagram-3 mb-2"></i>
            <p class="mb-1 fw-medium">Todavía no hay lectura de entradas.</p>
            <p class="mb-0 small text-muted">Usá el botón de actualizar para pedir `all_state` y cargar los niveles de las señales `in`.</p>
          </div>
        </section>
      </div>

      <div if={ !hasSignals() } class="text-center py-4 text-muted">
        <i class="bi bi-toggles mb-2" style="font-size: 2rem; opacity: 0.4;"></i>
        <p class="mb-0 small">No hay señales configuradas.<br>Pulsa el botón de actualizar.</p>
      </div>
    </div>
  </div>

  <script>
    import ChannelControl from './channel-control.riot';

    export default {
      components: {
        'channel-control': ChannelControl
      },

      hasSignals() {
        return this.getOutputChannels().length > 0 || this.getInputSignals().length > 0;
      },

      getOutputChannels() {
        return (this.props.device?.channels || []).filter(channel => typeof channel.channel === 'string' && channel.channel.startsWith('out'));
      },

      getInputChannels() {
        return (this.props.device?.channels || []).filter(channel => typeof channel.channel === 'string' && channel.channel.startsWith('in'));
      },

      getInputSignals() {
        const defaultInputs = [
          { channel: 'in0', name: 'Entrada 0', lastState: 'UNKNOWN' },
          { channel: 'in1', name: 'Entrada 1', lastState: 'UNKNOWN' }
        ];

        const channelInputs = this.getInputChannels();
        const inputMap = new Map(defaultInputs.map(channel => [channel.channel, channel]));

        channelInputs.forEach((channel) => {
          inputMap.set(channel.channel, {
            ...inputMap.get(channel.channel),
            ...channel
          });
        });

        const rawInputs = this.props.device?.data?.inputs || {};

        Object.keys(rawInputs).forEach((channelId) => {
          if (typeof channelId === 'string' && channelId.startsWith('in') && !inputMap.has(channelId)) {
            inputMap.set(channelId, {
              channel: channelId,
              name: channelId.toUpperCase(),
              lastState: String(rawInputs[channelId])
            });
            return;
          }

          if (typeof channelId === 'string' && channelId.startsWith('in') && inputMap.has(channelId)) {
            inputMap.set(channelId, {
              ...inputMap.get(channelId),
              lastState: String(rawInputs[channelId])
            });
          }
        });

        return Array.from(inputMap.values()).sort((left, right) => left.channel.localeCompare(right.channel));
      },

      hasInputReadings() {
        return !!this.props.device?.data?.inputs && Object.keys(this.props.device.data.inputs).length > 0;
      },

      getInputSignalValue(channelId) {
        const inputs = this.props.device?.data?.inputs;
        if (!inputs || !Object.prototype.hasOwnProperty.call(inputs, channelId)) {
          return null;
        }

        const value = inputs[channelId];
        return value === 1 || value === true || value === '1' || value === 'ON';
      },

      getInputSignalText(channelId) {
        const value = this.getInputSignalValue(channelId);
        if (value === null) return 'Sin lectura';
        return value ? 'Nivel alto' : 'Nivel bajo';
      },

      getInputSignalVariant(channelId) {
        const value = this.getInputSignalValue(channelId);
        if (value === null) return 'is-unknown';
        return value ? 'is-active' : 'is-inactive';
      },

      getInputSignalIcon(channelId) {
        const value = this.getInputSignalValue(channelId);
        if (value === null) return 'bi-question-circle';
        return value ? 'bi-arrow-up-circle-fill' : 'bi-arrow-down-circle';
      },

      getInputSignalHelp(channelId) {
        const value = this.getInputSignalValue(channelId);
        if (value === null) return 'Todavía no hay una lectura recibida para esta entrada.';
        return value
          ? 'La entrada está activa según la última lectura devuelta por el dispositivo.'
          : 'La entrada está inactiva según la última lectura devuelta por el dispositivo.';
      },

      getActiveInputCount() {
        return this.getInputSignals().reduce((count, signal) => {
          return count + (this.getInputSignalValue(signal.channel) ? 1 : 0);
        }, 0);
      },

      handlePulseSeq(e) {
        if (e) e.preventDefault();
        if (this.props.onpulseseq) this.props.onpulseseq();
      }
    }
  </script>
</device-status-control>
